import { outcomeToStatusFilter } from '@flakiness/server/common/fql/extractor.js';
import { TestOutcomes } from '@flakiness/server/common/stats/testOutcomes.js';
import { wireOutcomesToOutcome, type WireTypes } from '@flakiness/server/common/wireTypes.js';
import { ContextType } from '@lit/context';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { consume, contexts } from '../contexts.js';
import { Linkifier } from '../linkifier.js';
import { PageCommit } from '../page-commit.js';
import { linkStyles } from './cssstyles.js';


export type BaselineType = 'duration' | 'testruns';

@customElement('fk-commitstats')
export class FKCommitStats extends LitElement {
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;

  @property({ attribute: false }) stats?: WireTypes.CommitStats;
  @property({ attribute: false }) commit?: WireTypes.Commit;
  @property({ attribute: false }) baseline?: number;
  @property({ attribute: false }) baselineType?: BaselineType;
  @property({ attribute: false }) runlinker?: Linkifier<WireTypes.WellformedRun>;
  @property({ type: Boolean }) highlight = false;
  @property({ type: Boolean }) noduration = false;

  override render() {
    const commitStats = this.stats;
    const commit = commitStats?.commit ?? this.commit;
    if (!commit)
      return nothing;

    if (!commitStats) {
      return html`
        <h-box class=container style="gap: var(--sl-spacing-x-large);">
          <sl-icon name=cloud-slash style="color: var(--sl-color-neutral-400); font-size: var(--sl-font-size-large);"></sl-icon>
          <fk-commit .project=${this._project} .commit=${commit}></fk-commit>
        </h-box>
      `;
    }

    const outcome = wireOutcomesToOutcome(commitStats.testStats, this._project?.acceptableFlakinessRatio ?? 0);
    const isSingleTest = TestOutcomes.sumAllCounts(commitStats.testStats) <= 1;
    const project = this._project;
    const outcomeLinkifier = project ? (o: WireTypes.Outcome) => PageCommit.url({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
      sha: commit.commitId,
      query: `status:${outcomeToStatusFilter(o)}`,
      snapshotId: this._snapshotId,
    }) : undefined;
    return html`
      <h-box class=${classMap({ container: true, [outcome ?? 'untested']: this.highlight })} style="gap: var(--sl-spacing-x-large);">
        ${isSingleTest ? html`
          <fk-outcome outcome=${outcome}></fk-outcome>
        ` : html`
          <outcomes-bar .counts=${commitStats.testStats} .linkifier=${outcomeLinkifier}></outcomes-bar>
        `}

        <fk-commit .project=${this._project} .commit=${commitStats.commit}>
          <sl-icon slot=details name=dot></sl-icon>
          <runstats-selector slot=details
            .commitStats=${commitStats}
            .runlinker=${this.runlinker}
          ></runstats-selector>
        </fk-commit>

        <x-filler></x-filler>

        ${this.baselineType === 'testruns' ? html`
          ${this.baseline !== undefined ?
            html`<fk-number-trend .value=${TestOutcomes.sumAllCounts(commitStats.testStats)} .baseline=${this.baseline}></fk-number-trend>` :
            html`<int-num .x=${TestOutcomes.sumAllCounts(commitStats.testStats)}></int-num>`
          }
        ` : commitStats.durationMs === undefined || this.noduration ? nothing :
          this.baseline !== undefined ?
            html`<fk-duration-trend .ms=${commitStats.durationMs} .baseline=${this.baseline} duration></fk-duration-trend>` :
            html`<time-interval .ms=${commitStats.durationMs}></time-interval>`
        }
      </h-box>
    `;
  }

  static styles = [linkStyles, css`
    fk-duration-trend, fk-number-trend, time-interval, int-num {
      flex: none;
    }

    fk-commit {
      min-width: 40ch;
      flex: auto;
    }

    :host {
      display: block;
      border: 1px solid var(--fk-color-border);
      border-radius: var(--sl-border-radius-large);
    }

    .container {
      padding: var(--sl-spacing-small) var(--sl-spacing-medium);

      &.regressed {
        background-color: color-mix(in oklch, var(--fk-color-outcome-regressed), white 90%);
      }
      &.unexpected {
        background-color: color-mix(in oklch, var(--fk-color-outcome-unexpected), white 90%);
      }
      &.skipped {
        background-color: color-mix(in oklch, var(--fk-color-outcome-skipped), white 90%);
      }
      &.flaked {
        background-color: color-mix(in oklch, var(--fk-color-outcome-flaked), white 90%);
      }
      &.expected {
        background-color: color-mix(in oklch, var(--fk-color-outcome-expected), white 90%);
      }
    }
  `];
}

